import { computed } from 'vue'

import type { InsurancePolicy } from '@backmarket/http-api'
import type { CartItem } from '@backmarket/http-api/src/api-specs-checkout/cart/cart.types'
import type {
  Order,
  Orderline,
  PaymentResult,
} from '@backmarket/http-api/src/api-specs-payment/payment/payment-result.types'
import type { PaymentMethod } from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import { usePaymentAdvertisement } from '@backmarket/nuxt-layer-payment/advertising/composables/usePaymentAdvertisement'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import type { EventData } from '@backmarket/nuxt-module-tracking'
import { formatPrice } from '@backmarket/nuxt-module-tracking/formatPrice'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'

import { useCartStore } from '~/scopes/checkout/stores/cartStore'
import { getItemsWithEligiblePromoCodes } from '~/scopes/checkout/utils/flash-sales'

const CUSTOMER_TYPE = {
  EXISTING: 'existing',
  PROSPECT: 'prospect',
}
const PRODUCT_DEAL_TYPE = {
  NORMAL: 'normal',
}

export function getInsurancePolicy(
  orderlineId: number,
  insurancePolicies?: InsurancePolicy[],
) {
  if (!insurancePolicies) return null

  return insurancePolicies.find(
    (insurancePolicy) => insurancePolicy.orderlineId === orderlineId,
  )
}

export function getPaymentType(insurancePolicy?: InsurancePolicy | null) {
  if (!insurancePolicy) return null

  return insurancePolicy.isMonthly ? 'monthly' : 'upfront'
}

export const formatProduct = (
  order: Order,
  orderline: Orderline,
  advertisedMethodsId: string,
  isFlashSales?: boolean,
) => {
  let productId = `${orderline.product?.id}`
  let productPrice

  const insurancePolicy = getInsurancePolicy(
    orderline.id,
    orderline.coverage?.insurancePolicies,
  )

  try {
    const match = (orderline?.product?.slug ?? []).match(/(\d+)\.html/)
    productId = match ? match[1] : ''
    productPrice =
      parseFloat(orderline.price.amount ?? 0) / (orderline.quantity ?? 1)
  } catch {
    // fails silently
  }

  return {
    brand: orderline.product?.brand,
    category: orderline.product?.category,
    color: orderline.product?.color,
    dealType: PRODUCT_DEAL_TYPE.NORMAL,
    specialOfferType: orderline.product?.offerType ?? 0,
    id: productId,
    listingId: orderline.product?.listingId,
    merchantId: order.merchant?.id,
    model: orderline.product?.model,
    name: orderline.product?.title,
    price: productPrice,
    currency: orderline?.price.currency,
    quantity: orderline?.quantity,
    shipper: [
      orderline?.shipping?.shipperId,
      orderline?.shipping?.deliveryDelay,
      formatPrice(orderline?.priceShipping),
    ].join(' - '),
    shippingDelay: orderline?.shipping?.deliveryDelay,
    shippingPrice: orderline?.priceShipping.amount,
    splitPayment: advertisedMethodsId,
    variant: orderline.product?.gradeId,
    warrantyDuration:
      (orderline?.coverage?.merchantWarranty?.duration ?? 0) +
      (orderline?.coverage?.backmarketWarranty?.duration ?? 0),
    insuranceTitle: insurancePolicy?.offerTitle,
    insurancePrice: insurancePolicy?.price?.amount,
    insurancePaymentType: getPaymentType(insurancePolicy),
    searchRelatedQuery: orderline?.searchRelatedQuery ?? {},
    uuid: orderline.product?.akeneoId,
    mobilePlanOfferSelected: orderline.mobilePlan?.offer?.name ?? '',
    mobilePlanOfferSelectedType: orderline.mobilePlan?.offer?.type ?? '',
    isFlashSales,
  }
}

const paymentTrackingData = ({
  payment,
  lastOrderDate,
  numberOfOrders,
  transactionDate,
}: {
  payment: PaymentResult
  lastOrderDate: Date | null
  numberOfOrders: number
  transactionDate: string
}) => {
  const newCommission = (
    (payment.orders.reduce(
      (acc, order) =>
        acc +
        order.orderlines.reduce(
          (accOrderline, orderline) =>
            accOrderline + parseFloat(orderline?.price?.amount ?? 0),
          0.0,
        ),
      0.0,
    ) +
      parseFloat(payment.priceShipping.amount)) *
    0.1
  ).toFixed(2)

  return {
    code_parrain:
      ((payment.discountDetails?.isGodfather ?? false) &&
        payment.discountDetails?.token) ??
      '',
    revenue: payment.amount.amount,
    discount: payment.discountDetails?.token ?? '',
    commission: payment.commission.amount,
    '10Pct_FEE': `${newCommission}`,
    orderId: payment.groupOrderId,
    paymentMethod: payment.method.bmCode,
    shipping: payment.priceShipping.amount,
    swap_details: payment?.sourcingOrders[0],
    swap: !isEmpty(payment.sourcingOrders),
    swapCategory: payment?.sourcingOrders?.[0]?.category,
    swapEstimation: !isEmpty(payment?.sourcingOrders?.[0]?.price.amount)
      ? parseInt(payment.sourcingOrders[0].price.amount, 10)
      : 0,
    totalQuantity: payment.orders.reduce(
      (acc, order) =>
        acc +
        order.orderlines.reduce(
          (accOrderline, orderline) => accOrderline + orderline.quantity,
          0,
        ),
      0,
    ),
    transactionDate,
    transactionId: payment.paymentId,
    customerType:
      lastOrderDate !== null && numberOfOrders > 0
        ? CUSTOMER_TYPE.EXISTING
        : CUSTOMER_TYPE.PROSPECT,
    monthsSinceLastPurchase: payment.client?.monthsSinceLastPurchase,
    revenueBM: (
      Number(payment.commission.amount) -
      Number(payment.serviceFee?.amount || 0)
    ).toFixed(2),
    serviceFee: Number(payment.serviceFee?.amount || 0).toFixed(2),
  }
}

const productsTrackingData = (
  payment: PaymentResult,
  advertisedMethodsId: string,
) => {
  const cartStore = useCartStore()

  const flashSalesEligibleItems = computed(() =>
    getItemsWithEligiblePromoCodes(cartStore.items),
  )

  const eligibleIds =
    flashSalesEligibleItems.value?.map((item: CartItem) => item.listingId) ?? []

  return payment.orders
    .map((order) =>
      order.orderlines.map((orderline) => {
        const isFlashSales = eligibleIds.includes(
          String(orderline.product?.listingId),
        )

        return formatProduct(
          order,
          orderline,
          advertisedMethodsId,
          isFlashSales,
        )
      }),
    )
    .flat(2)
}

const postPurchaseTrackingData = (payment: PaymentResult) => {
  return {
    orderId: payment.groupOrderId ?? '',
    productName: payment.postPurchase?.productTitle ?? '',
    productOrderlineId: payment.postPurchase?.insurancePolicy.orderlineId ?? '',
    productInsuranceTitle: payment.postPurchase?.insuranceOffer.title ?? '',
    productInsurancePrice:
      payment.postPurchase?.insurancePolicy.price.amount ?? '',
  }
}

export const usePaymentResultEventTracker = (payment: PaymentResult) => {
  const i18n = useI18n()
  const {
    user: { lastOrderDate, numberOfOrders },
  } = useUserStore()

  const paymentAdvertisementRef = usePaymentAdvertisement('product', [
    payment.method as PaymentMethod,
  ])

  const {
    trackPaymentResultFailure,
    trackPaymentResultSuccess,
    trackPostPurchasePayment,
  } = useTracking()

  const advertisedMethodsId = paymentAdvertisementRef.value.isAdvertised
    ? paymentAdvertisementRef.value.methodName
    : ''

  const trackPaymentSuccess = () => {
    if (payment && payment.success) {
      if (!isEmpty(payment.postPurchase)) {
        trackPostPurchasePayment(postPurchaseTrackingData(payment))
      } else {
        trackPaymentResultSuccess({
          payment: paymentTrackingData({
            lastOrderDate: lastOrderDate as Date,
            numberOfOrders,
            payment,
            transactionDate: i18n.date(new Date(payment.date)),
          }),
          products: productsTrackingData(
            payment,
            advertisedMethodsId,
          ) as EventData[],
        })
      }
    }
  }

  const trackPaymentFailure = () => {
    if (payment && !payment.success) {
      trackPaymentResultFailure({
        paymentMethod: payment.method?.bmCode,
        errorCode: payment.errorCode,
        errorReason: payment.errorMessage,
      })
    }
  }

  return {
    paymentSuccess: trackPaymentSuccess,
    paymentFailure: trackPaymentFailure,
  }
}
